import { removeAccents } from '../utils/utils';

export type SuggestionType = {
    label: string,
    value: string,
    isFake?: boolean,
};

type DatasetType = Promise<SuggestionType[]> | ((search: string, abortSignal: AbortSignal|undefined) => Promise<SuggestionType[]>);

type SuggestionSelectOptions = {
    nbCharBeforeSearch: number,
    nbResultsToShow: number,
    dataset: DatasetType,
    useCache: boolean,
    noResultsText: string,
    delayForTypingInMS: number,
};

const defaultOptions = {
    nbCharBeforeSearch: 3,
    nbResultsToShow: 5,
    dataset: Promise.resolve([]),
    useCache: false,
    noResultsText: 'Aucun résultats',
    delayForTypingInMS: 500,
};

/**
 * Permet d'afficher des suggestions comme un select.
 * Le container prend la classe `is-valid` lorsque le contenu de l'input vient d'une suggestion
 * 
 * @param {HTMLInputElement} $input
 * @param {Partial<SuggestionSelectOptions>} options
 * 
 * @param {number} options.nbCharBeforeSearch Nombre de caractères minimum avant d'effectuer une recherche [default: 3]
 * @param {number} options.nbResultsToShow Nombre de suggestions à afficher [default: 5]
 * @param {DatasetType} options.dataset Tableau contenant les suggestions possibles, ou fonction retournant les suggestions possibles [default: []]
 * @param {boolean} options.useCache Utilisation d'un cache interne (conseillé si recherche ajax) [default: false]
 * @param {string} options.noResultsText Texte affiché lorsqu'aucun résultats n'est trouvé [default: 'Aucun résultats']
 * @param {number} options.delayForTypingInMS Nombre de millisecondes à attendre pour laisser le temps à l'utilisateur d'écrire un mot [default: 500]
 */
export class SuggestionSelect {
    $container: HTMLDivElement = document.createElement('div');
    $input: HTMLInputElement;
    $loader: HTMLSpanElement = document.createElement('span');
    $suggestionList: HTMLUListElement = document.createElement('ul');
    options: SuggestionSelectOptions;
    abortController: AbortController|null = null;
    timeoutCallback: ReturnType<typeof setTimeout>|null = null;
    oldValue: string = '';
    cache: Record<string, SuggestionType[]> = {};

    constructor ($input: HTMLInputElement, options: Partial<SuggestionSelectOptions> = {}) {
        this.$input = $input;
        this.options = { ...defaultOptions, ...options };

        this.hideSuggestions();
        this.initDOM();

        // Recherche dans les suggestions lorsque l'on écrit dans l'input
        this.$input.addEventListener('input', () => {
            this.searchForSuggestions();
        });

        // Affiche les suggestions lorsque le focus entre dans l'input
        this.$container.addEventListener('focusin', () => {
            if (this.$suggestionList.children.length) {
                this.showSuggestions();
            }
        });

        // Masque les suggestions lorsque le focus sort de l'input
        // (Le timeout, c'est pour laisser le temps au navigateur de bouger le focus)
        this.$container.addEventListener('focusout', () => {
            setTimeout(() => {
                if (
                    !this.isSuggestionsHidden() &&
                    (!document.activeElement || !this.$container.contains(document.activeElement))
                ) {
                    this.hideSuggestions();
                }
            }, 100);
        });

        // Sélectionne la suggestion au click
        this.$suggestionList.addEventListener('click', evt => {
            const $target = evt.target as HTMLButtonElement;

            if (!$target || $target.nodeName !== 'BUTTON') {
                return;
            }

            this.selectButton($target);
        });

        this.$container.addEventListener('keydown', evt => {
            // Masque les suggestions lors de l'appuie sur la touche Echap
            if (evt.key === 'Escape') {
                this.hideSuggestions();
                return;
            }

            // Sélectionne la suggestion focus lors de l'appuie sur la touche Enter
            if (evt.key === 'Enter' && document.activeElement && document.activeElement.nodeName === 'BUTTON') {
                this.selectButton(document.activeElement as HTMLButtonElement);
            }
        });
    }

    /**
     * Sélection d'une suggestion
     */
    selectButton($suggestionButton: HTMLButtonElement): void {
        this.$input.value = $suggestionButton.dataset.value || $suggestionButton.innerText;
        this.removeSuggestions();

        this.$container.classList.add('is-valid');
    }

    /**
     * Initialisation du DOM
     */
    initDOM(): void {
        this.$container.classList.add('suggestion-select');
        this.$input.setAttribute('autocomplete', 'off');
        this.$input.setAttribute('autocapitalize', 'off');
        this.$input.setAttribute('spellcheck', 'false');
        this.$loader.classList.add('loader');
        this.$loader.setAttribute('role', 'presentation');
        this.$suggestionList.classList.add('suggestions-list');

        // Insertion dans le DOM
        (this.$input.parentElement as HTMLElement).insertBefore(this.$container, this.$input);
        this.$container.append(this.$input, this.$loader, this.$suggestionList);

        // Remplissage des suggestions dans le cas ou `this.options.dataset` n'est pas une fonction
        if (typeof this.options.dataset !== 'function') {
            this.getSuggestions('').then(suggestions => {
                this.addSuggestionsInDOM(suggestions);
            }).catch(() => {}).finally(() => {
                this.hideLoading();
            });
        }
    }

    /**
     * Effectue une recherche dans `this.options.dataset`
     */
    searchForSuggestions(): void {
        const search = this.$input.value.trim();

        if (search === this.oldValue) {
            return;
        }

        this.removeSuggestions();

        this.oldValue = search;
        this.$container.classList.remove('is-valid');

        // Annulation du lancement différé de la recherche précédente
        if (this.timeoutCallback) {
            clearTimeout(this.timeoutCallback);
        }

        // Annulation requête précédente si elle n'est pas terminée
        this.abortController?.abort();

        // On n'effectue pas de recherche tant qu'un nombre de caractère minimum n'est pas atteint
        if (search.length < this.options.nbCharBeforeSearch) {
            this.hideLoading();
            return;
        }

        if (this.options.useCache && typeof this.cache[search] !== 'undefined') {
            // Affichage immédiat des résultats du cache
            this.addSuggestionsInDOM(this.cache[search]);
            this.showSuggestions();

            this.hideLoading();
        } else {
            this.showLoading();

            if (typeof this.options.dataset === 'function') {
                // On attend 5 secondes avant de lancer la recherche
                // pour laisser l'utilisateur continuer de taper un mot s'il le souhaite
                this.timeoutCallback = setTimeout(() => {
                    this.search(search);
                }, this.options.delayForTypingInMS);
            } else {
                this.search(search);
            }
        }
    }

    search(search: string): void {
        this.getSuggestions(search).then(suggestions => {
            this.saveInCache(search, suggestions);
            this.addSuggestionsInDOM(suggestions);
            this.showSuggestions();
        }).catch(() => {}).finally(() => {
            this.hideLoading();
            this.abortController = null;
        });
    }

    saveInCache(search: string, suggestions: SuggestionType[]): void {
        if (!this.options.useCache) {
            return;
        }

        // Sauvegarde les suggestions en cache
        this.cache[search] = suggestions;
    }

    addSuggestionsInDOM(suggestions: SuggestionType[]): void {
        const $suggestionElements: HTMLLIElement[] = [];
        suggestions.forEach(suggestion => {
            const $item = document.createElement('li');
            $item.classList.add('suggestion-item');

            if (!suggestion.isFake) {
                const $link = document.createElement('button');
                $link.setAttribute('type', 'button');
                $link.dataset.value = suggestion.value;
                $link.innerText = suggestion.label;

                $item.append($link);
            } else {
                $item.classList.add('fake-item');
                $item.innerText = suggestion.label;
            }

            $suggestionElements.push($item);
        });
        this.$suggestionList.append(...$suggestionElements.splice(0, this.options.nbResultsToShow));

        if (this.$suggestionList.children.length === 0) {
            this.addSuggestionsInDOM([{
                label: this.options.noResultsText,
                value: '',
                isFake: true,
            }]);
        }
    }

    async getSuggestions(search: string): Promise<SuggestionType[]> {
        if (typeof this.options.dataset === 'function') {
            this.abortController = new AbortController();
            return await this.options.dataset(search, this.abortController?.signal);
        }

        return await this.options.dataset.then(dataset => {
            return dataset.filter(data => this.isStrMatch(search, data.label));
        });
    }

    cleanStrForSearch(str: string): string {
        return removeAccents(str)
            .toLowerCase()
            .replace(/-/g, ' ');
    }

    isStrMatch(str: string, str2: string): boolean {
        const regex = new RegExp(this.cleanStrForSearch(str));
        return regex.test(this.cleanStrForSearch(str2));
    }

    removeSuggestions(): void {
        this.hideSuggestions();
        this.$suggestionList.innerHTML = '';
    }

    isSuggestionsHidden(): boolean {
        return this.$suggestionList.style.display === 'none';
    }

    showSuggestions(): void {
        this.$suggestionList.style.display = '';
        this.$suggestionList.setAttribute('aria-hidden', 'false');
        this.$container.classList.add('suggestions-shown');
    }

    hideSuggestions(): void {
        this.$suggestionList.style.display = 'none';
        this.$suggestionList.setAttribute('aria-hidden', 'true');
        this.$container.classList.remove('suggestions-shown');
    }

    showLoading(): void {
        this.$container.classList.add('loading');
    }

    hideLoading(): void {
        this.$container.classList.remove('loading');
    }
}
